import { useEffect, useState } from 'react';
import { Layout } from '../components/Layout';
import api from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Plus, Search, Edit2, Trash2, Wrench } from 'lucide-react';
import { cn } from '../lib/utils';

const emptyForm = () => ({
  company: '',
  contact_name: '',
  address: '',
  email: '',
  phone: '',
  specialty: '',
  notes: '',
});

export default function ContractorsPage() {
  const [contractors, setContractors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [saving, setSaving] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = async () => {
    const res = await api.get('/contractors');
    setContractors(res.data);
    setSelected(current => current ? res.data.find(c => c.id === current.id) || null : null);
  };

  useEffect(() => {
    load().catch(() => toast.error('Dienstleister konnten nicht geladen werden')).finally(() => setLoading(false));
  }, []);

  const normalizedSearch = search.trim().toLowerCase();
  const filtered = contractors.filter(c => !normalizedSearch || [
    c.company, c.contact_name, c.address, c.email, c.phone, c.specialty, c.notes,
  ].some(v => (v || '').toString().toLowerCase().includes(normalizedSearch)));

  const openNew = () => {
    setEditing(null);
    setForm(emptyForm());
    setDlgOpen(true);
  };

  const openEdit = (contractor) => {
    setEditing(contractor);
    setForm({
      company: contractor.company || '',
      contact_name: contractor.contact_name || '',
      address: contractor.address || '',
      email: contractor.email || '',
      phone: contractor.phone || '',
      specialty: contractor.specialty || '',
      notes: contractor.notes || '',
    });
    setDlgOpen(true);
  };

  const handleSave = async () => {
    if (!form.company.trim()) {
      toast.error('Firma ist Pflicht');
      return;
    }
    setSaving(true);
    try {
      if (editing) {
        await api.put(`/contractors/${editing.id}`, form);
        toast.success('Dienstleister aktualisiert');
      } else {
        await api.post('/contractors', form);
        toast.success('Dienstleister erstellt');
      }
      setDlgOpen(false);
      await load();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Dienstleister wirklich löschen?')) return;
    await api.delete(`/contractors/${id}`);
    toast.success('Gelöscht');
    await load();
  };

  return (
    <Layout>
      <div className="p-6 md:p-8" data-testid="contractors-page">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="font-heading font-black text-3xl">Dienstleister</h1>
            <p className="text-sm text-muted-foreground mt-0.5">{contractors.length} Dienstleister / Monteure</p>
          </div>
          <Button onClick={openNew} className="rounded-sm gap-2" data-testid="new-contractor-btn">
            <Plus className="w-4 h-4" /> Neuer Dienstleister
          </Button>
        </div>

        <div className="flex gap-4">
          <div className="flex-1 min-w-0">
            <div className="relative mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" strokeWidth={1.5} />
              <Input value={search} onChange={e => setSearch(e.target.value)} placeholder="Dienstleister suchen..." className="pl-9 rounded-sm" data-testid="contractor-search" />
            </div>
            {loading ? (
              <div className="flex items-center justify-center h-40"><div className="w-5 h-5 border-2 border-primary border-t-transparent rounded-full animate-spin" /></div>
            ) : (
              <div className="space-y-1.5">
                {filtered.map(contractor => (
                  <div
                    key={contractor.id}
                    onClick={() => setSelected(contractor)}
                    className={cn(
                      'flex items-center justify-between p-4 border rounded-sm cursor-pointer transition-all',
                      selected?.id === contractor.id ? 'border-primary bg-primary/5' : 'border-border/60 bg-card hover:border-primary/30 hover:shadow-sm'
                    )}
                  >
                    <div className="min-w-0">
                      <p className="font-medium text-sm text-foreground">{contractor.company}</p>
                      {contractor.contact_name && <p className="text-xs text-muted-foreground truncate">{contractor.contact_name}</p>}
                      {[contractor.email, contractor.phone].filter(Boolean).length > 0 && (
                        <p className="text-xs text-muted-foreground truncate">{[contractor.email, contractor.phone].filter(Boolean).join(' · ')}</p>
                      )}
                    </div>
                    <div className="flex items-center gap-2 ml-3">
                      <button onClick={e => { e.stopPropagation(); openEdit(contractor); }} className="text-muted-foreground hover:text-foreground transition-colors p-1.5 rounded-sm hover:bg-muted">
                        <Edit2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                      <button onClick={e => { e.stopPropagation(); handleDelete(contractor.id); }} className="text-muted-foreground hover:text-destructive transition-colors p-1.5 rounded-sm hover:bg-destructive/10">
                        <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} />
                      </button>
                    </div>
                  </div>
                ))}
                {filtered.length === 0 && <p className="text-sm text-muted-foreground text-center py-8">Keine Dienstleister gefunden</p>}
              </div>
            )}
          </div>

          {selected && (
            <div className="w-80 shrink-0 bg-card border border-border/60 rounded-sm p-5 h-fit">
              <h3 className="font-heading font-bold text-base mb-3 flex items-center gap-2">
                <Wrench className="w-4 h-4 text-primary" strokeWidth={1.5} />
                {selected.company}
              </h3>
              {selected.contact_name && <p className="text-sm text-muted-foreground mb-1">{selected.contact_name}</p>}
              {selected.specialty && <p className="text-sm text-muted-foreground mb-1">{selected.specialty}</p>}
              {selected.address && <p className="text-sm text-muted-foreground mb-1 whitespace-pre-line">{selected.address}</p>}
              {selected.email && <p className="text-sm text-muted-foreground mb-1">{selected.email}</p>}
              {selected.phone && <p className="text-sm text-muted-foreground mb-1">{selected.phone}</p>}
              {selected.notes && <p className="text-sm text-muted-foreground mt-3 pt-3 border-t border-border/60 whitespace-pre-line">{selected.notes}</p>}
            </div>
          )}
        </div>
      </div>

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent data-testid="contractor-dialog">
          <DialogHeader>
            <DialogTitle className="font-heading">{editing ? 'Dienstleister bearbeiten' : 'Neuer Dienstleister'}</DialogTitle>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Firma *</Label><Input value={form.company} onChange={e => setForm(f => ({ ...f, company: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Ansprechperson</Label><Input value={form.contact_name} onChange={e => setForm(f => ({ ...f, contact_name: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label>Adresse</Label><Input value={form.address} onChange={e => setForm(f => ({ ...f, address: e.target.value }))} className="rounded-sm" /></div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>E-Mail</Label><Input type="email" value={form.email} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} className="rounded-sm" /></div>
              <div className="space-y-1.5"><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} className="rounded-sm" /></div>
            </div>
            <div className="space-y-1.5"><Label>Dienstleistung</Label><Input value={form.specialty} onChange={e => setForm(f => ({ ...f, specialty: e.target.value }))} placeholder="z.B. Schreiner, Gipser, Maler" className="rounded-sm" /></div>
            <div className="space-y-1.5"><Label>Notizen</Label><Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={3} className="rounded-sm resize-none" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)} className="rounded-sm">Abbrechen</Button>
            <Button onClick={handleSave} disabled={saving} className="rounded-sm">{saving ? 'Speichern...' : 'Speichern'}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </Layout>
  );
}
